import React from 'react';
import { Box, Typography, IconButton, Paper } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import { documentCardSx, terminalColors, terminalBarIconButtonSx } from '../../theme/terminalStyles';
import { formatMoney } from '../../helpers/dataFormatter';
import { saleDocTypeLabel } from '../../helpers/saleDocType';

const SALE_CARD_ACTION_SIZE = 36;
const SALE_CARD_STATUS_DOT_SIZE = 24;

const SaleDocumentCard = ({ sale, statusColor, pickingStatus, onStart, onDelete, markedDeleted }) => {
  const dateStr = sale.date ? new Date(sale.date).toLocaleDateString('uk-UA') : '—';
  const sp = [...(sale.spickings || [])].sort((a, b) => a.id - b.id)[0];
  const isFullyCollected = Boolean(sale.isCollected || sp?.isDone);
  const isManual = Boolean(sale.isManual);
  const titleNumber = isManual ? sale.number || sale.id : sale.number;
  const pickerBarcode = String(sale.pickerBarcode || sp?.pickerBarcode || '').trim();
  const typeLabel = saleDocTypeLabel(sale.dType);

  return (
    <Paper sx={documentCardSx} elevation={0}>
      <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.5 }}>
        <Box
          sx={{
            width: SALE_CARD_ACTION_SIZE,
            minWidth: SALE_CARD_ACTION_SIZE,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 0.25,
          }}
        >
          <IconButton
            onClick={onStart}
            disabled={!onStart}
            aria-label="Почати збірку"
            sx={{
              ...terminalBarIconButtonSx,
              width: SALE_CARD_ACTION_SIZE,
              height: SALE_CARD_ACTION_SIZE,
              minWidth: SALE_CARD_ACTION_SIZE,
              minHeight: SALE_CARD_ACTION_SIZE,
            }}
          >
            {isFullyCollected || !onStart ? <LockOutlinedIcon /> : <PlayArrowIcon />}
          </IconButton>
          <Box
            aria-hidden
            sx={{
              width: SALE_CARD_STATUS_DOT_SIZE,
              height: SALE_CARD_STATUS_DOT_SIZE,
              borderRadius: '50%',
              bgcolor: statusColor,
              flexShrink: 0,
              ...(pickingStatus === 'resume'
                ? { border: '1px solid rgba(0, 0, 0, 0.12)' }
                : {}),
            }}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {isManual
              ? `# ${titleNumber}`
              : `${typeLabel ? `${typeLabel} ` : ''}№ ${titleNumber}`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {dateStr}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }} noWrap title={sale.clientName}>
            {isManual ? 'Ручна збірка' : sale.clientName || '—'}
          </Typography>
          <Box
            sx={{
              mt: 1,
              display: 'flex',
              alignItems: 'baseline',
              gap: 2,
              minWidth: 0,
            }}
          >
            {isManual ? (
              <Typography variant="body2" color="text.secondary" noWrap title={pickerBarcode}>
                {pickerBarcode ? `Картка: ${pickerBarcode}` : sale.warehouse?.name || '—'}
              </Typography>
            ) : (
              <>
            <Typography variant="body1" fontWeight={600} sx={{ flexShrink: 0 }}>
              {formatMoney(sale.sum ?? 0)} ₴
            </Typography>
            {sale.ttnNumber ? (
              <Typography
                variant="body2"
                color="text.secondary"
                noWrap
                title={`ТТН: ${sale.ttnNumber}`}
                sx={{ minWidth: 0, flex: 1 }}
              >
                ТТН: {sale.ttnNumber}
              </Typography>
            ) : null}
              </>
            )}
          </Box>
        </Box>
        {onDelete ? (
          <IconButton
            onClick={onDelete}
            aria-label="помітити на видалення"
            sx={{
              alignSelf: 'center',
              color: 'error.main',
              flexShrink: 0,
            }}
          >
            <DeleteOutlineIcon />
          </IconButton>
        ) : markedDeleted ? (
          <Box
            aria-label="видалений"
            sx={{
              alignSelf: 'center',
              color: 'error.main',
              display: 'flex',
              flexShrink: 0,
            }}
          >
            <DeleteForeverOutlinedIcon />
          </Box>
        ) : null}
      </Box>
    </Paper>
  );
};

export default SaleDocumentCard;
